import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle2, Fingerprint, HardDriveDownload, LocateFixed, MapPin, QrCode, RefreshCw } from 'lucide-react';
import {
  ONLINE_PLATFORM_VALUES,
  Permissions as P,
  type ActualModality,
  type ClockResultDto,
  type OnlinePlatform,
  type TodaySessionDto,
  type WebauthnAssertionOptionsDto,
  type WorkArrangement,
} from '@fas/types';
import { Alert, Button, Field, Input, Select, StatusBadge } from '@fas/ui';
import { api, ApiError } from '@/lib/api';
import { useAuthStore } from '@/lib/auth-store';
import { deviceProof, offlineDeviceProof, prefetchDeviceChallenge } from '@/lib/device-proof';
import { currentSkewMs, queueClockEvent } from '@/lib/offline-queue';
import { labelize } from '@/lib/format';
import { getCurrentPosition, GPS_ERROR_MESSAGES, type GpsFailure, type GpsLocation } from '@/lib/geolocation';
import { useCan } from '@/lib/permissions';
import { getAssertion, WebauthnProblem } from '@/lib/webauthn';
import { useMyWebauthnCredentials } from '@/features/account/BiometricsCard';
import { MODALITY_LABELS } from '@/features/scheduling/labels';
import { ACTUAL_MODALITY, clockRange, clockTime, LOCATION_STATUS, TIME_STATUS, CLOCK_OUT_STATUS, WORK_ARRANGEMENT, ATTENDANCE_STATUS } from './labels';
import { usePublicSettings } from './hooks';

const LOCATION_MODES = new Set<ActualModality>(['ONSITE', 'HYBRID_COMBINATION', 'FIELD_CLINICAL']);
const ONLINE_MODES = new Set<ActualModality>(['ONLINE', 'HYBRID_COMBINATION']);

type Gps = { state: 'idle' } | { state: 'locating' } | { state: 'ok'; location: GpsLocation } | { state: 'error'; error: GpsFailure };

function isCoarsePointer(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
}

/**
 * The device proof for a clock request (Phase 2B). Without a connection the request fails
 * anyway; any other problem sends the request without a proof and the server's device rule
 * decides, so a device check never blocks attendance where the policy does not require it.
 */
async function proofFor(kind: 'in' | 'out', requestId: string) {
  try {
    return await deviceProof(kind === 'in' ? 'CLOCK_IN' : 'CLOCK_OUT', requestId);
  } catch (err) {
    if (err instanceof ApiError && err.code === 'NETWORK_ERROR') throw err;
    return null;
  }
}

/** Reuse prefetched confirmation options this long (the server accepts the challenge for 5 minutes). */
const OPTIONS_REUSE_MS = 4 * 60_000;

const assertionOptions = (kind: 'in' | 'out', requestId: string) =>
  api.post<WebauthnAssertionOptionsDto>('/webauthn/assertion-options', { purpose: kind === 'in' ? 'CLOCK_IN' : 'CLOCK_OUT', requestId });

export function sessionTitle(s: TodaySessionDto): string {
  if (s.sessionType === 'DAILY') return `Daily attendance${s.dailySegment && s.dailySegment !== 'FULL_DAY' ? ` (${s.dailySegment})` : ''}`;
  return `${s.courseCode ?? ''} ${s.sectionCode ?? ''}`.trim();
}

/**
 * Clock-in / clock-out confirmation (§55.3, §55.4). Location is captured once, only when the
 * chosen mode needs it (§14.4). A retry of the same request reuses its idempotency key, and
 * success is shown only after the server confirms (§63 "never pretend attendance succeeded").
 * Without a connection, where the policy allows it, the event can be saved on the device and
 * sent later for review (§57, Phase 2D) — `offlineOnly` records it that way directly. With
 * `scan`, the event carries a scanned room code as presence evidence (Phase 2E).
 */
export function ClockDialog({
  session,
  kind,
  timeZone,
  onDone,
  onClose,
  offlineOnly = false,
  offlineUserId,
  onSaved,
  scan,
}: {
  session: TodaySessionDto;
  kind: 'in' | 'out';
  timeZone: string;
  onDone: (result: ClockResultDto) => void;
  onClose: () => void;
  /** Record on the device only (the app started without a connection). */
  offlineOnly?: boolean;
  /** Owner of the recorded event when nobody is signed in yet (offline start). */
  offlineUserId?: string;
  onSaved?: () => void;
  /** A room or kiosk code scanned for this event (Phase 2E), or a room tag tapped (`TAG`, Phase 3G). */
  scan?: { receipt: string; displayName: string; scannedAt: string; kind?: 'CODE' | 'TAG' };
}) {
  const isDaily = session.sessionType === 'DAILY';
  const { actions } = session;
  const [mode, setMode] = useState<ActualModality | null>(kind === 'in' ? (actions.allowedModes[0] ?? null) : session.actualModality);
  const [arrangement, setArrangement] = useState<WorkArrangement>(kind === 'in' ? 'ONSITE' : (session.workArrangement ?? 'ONSITE'));
  const [platform, setPlatform] = useState<OnlinePlatform | ''>(session.onlinePlatform ?? '');
  const [meetingRef, setMeetingRef] = useState('');
  const [gps, setGps] = useState<Gps>({ state: 'idle' });
  const [result, setResult] = useState<ClockResultDto | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const request = useRef<{ key: string; id: string } | null>(null);
  const settings = usePublicSettings();
  const disclosure = typeof settings.data?.['privacy.gps_disclosure_text'] === 'string' ? (settings.data['privacy.gps_disclosure_text'] as string) : null;

  // User verification (Phase 3H): where the policy asks, the device confirms its user with its
  // fingerprint, face or PIN before the request is sent. The challenge belongs to the request id,
  // so the first request's id and options are ready when the dialog opens: browsers let a
  // confirmation prompt follow the tap on the button only shortly.
  const uvRule = offlineOnly ? 'OFF' : actions.userVerification;
  const canSetUp = useCan(P.DEVICE_REGISTER_SELF);
  const credentials = useMyWebauthnCredentials(uvRule !== 'OFF' && canSetUp);
  const hasCredential = !!credentials.data?.some((c) => c.status === 'ACTIVE');
  const [skipVerification, setSkipVerification] = useState(false);
  const verifying = uvRule !== 'OFF' && hasCredential && !skipVerification;
  const [firstRequestId] = useState(() => crypto.randomUUID());
  const prefetched = useRef<{ requestId: string; at: number; options: Promise<WebauthnAssertionOptionsDto> } | null>(null);
  useEffect(() => {
    if (!verifying || prefetched.current) return;
    const options = assertionOptions(kind, firstRequestId);
    options.catch(() => undefined);
    prefetched.current = { requestId: firstRequestId, at: Date.now(), options };
  }, [verifying, kind, firstRequestId]);
  // Skipping the confirmation is offered only where the policy does not refuse the event.
  const mayContinueUnconfirmed = uvRule === 'FLAG' || (uvRule === 'REQUIRE' && kind === 'out');

  const confirmUser = async (requestId: string) => {
    const p = prefetched.current;
    const options =
      p && p.requestId === requestId && Date.now() - p.at < OPTIONS_REUSE_MS ? await p.options.catch(() => assertionOptions(kind, requestId)) : await assertionOptions(kind, requestId);
    prefetched.current = null;
    return getAssertion(options);
  };

  // Mirrors the server rule; the server makes the final decision (§14.3).
  const policyWantsGps = actions.locationRequired || (kind === 'in' && !isDaily && !LOCATION_MODES.has(actions.allowedModes[0] ?? 'ONLINE'));
  const needsLocation = isDaily
    ? arrangement === 'ONSITE' && actions.locationRequired
    : kind === 'out'
      ? actions.locationRequired
      : !!mode && LOCATION_MODES.has(mode) && policyWantsGps;
  const showsOnline = !isDaily && kind === 'in' && !!mode && ONLINE_MODES.has(mode);

  const locate = useCallback(async () => {
    setGps({ state: 'locating' });
    const res = await getCurrentPosition();
    setGps(res.ok ? { state: 'ok', location: res.location } : { state: 'error', error: res.error });
  }, []);

  useEffect(() => {
    if (needsLocation && gps.state === 'idle') void locate();
  }, [needsLocation, gps.state, locate]);

  // Ready by the time the faculty member confirms (usually while the GPS fix is taken).
  useEffect(() => {
    if (!offlineOnly) prefetchDeviceChallenge();
  }, [offlineOnly]);

  const payloadFor = (): Record<string, unknown> => {
    const where = needsLocation
      ? gps.state === 'ok'
        ? { location: gps.location }
        : { gpsError: gps.state === 'error' ? gps.error : 'UNAVAILABLE' }
      : {};
    const method = scan ? (scan.kind === 'TAG' ? 'NFC_TAG' : 'QR_CODE') : isCoarsePointer() ? 'MOBILE_GPS' : 'WEB_BROWSER';
    const evidence = scan ? { scan: { receipt: scan.receipt } } : {};
    return kind === 'in'
      ? {
          sessionId: session.id,
          sessionType: isDaily ? 'DAILY' : 'CLASS',
          ...(isDaily ? { workArrangement: arrangement } : { actualModality: mode }),
          ...(showsOnline ? { onlinePlatform: platform || null, onlineMeetingRef: meetingRef.trim() || null } : {}),
          ...where,
          method,
          ...evidence,
        }
      : { sessionId: session.id, ...where, method, ...evidence };
  };

  // `always`: offline, fail at once (and offer saving on the device) instead of pausing.
  const submit = useMutation({
    networkMode: 'always',
    mutationFn: async () => {
      const payload = payloadFor();
      // Same request → same idempotency key, so a retry after a network failure is safe (§12.4).
      const key = JSON.stringify(payload);
      if (request.current?.key !== key) request.current = { key, id: request.current ? crypto.randomUUID() : firstRequestId };
      const clientRequestId = request.current.id;
      // The confirmation first, closest to the tap that asked for it.
      const userVerification = verifying ? await confirmUser(clientRequestId) : null;
      const device = await proofFor(kind, clientRequestId);
      return api.post<ClockResultDto>(`/attendance/clock-${kind}`, {
        ...payload,
        clientRequestId,
        clientTimestamp: new Date().toISOString(),
        ...(device ? { device } : {}),
        ...(userVerification ? { userVerification } : {}),
      });
    },
    onSuccess: (res) => {
      setResult(res);
      onDone(res);
    },
  });

  // §57: recorded on the device, sent when back online, always reviewed before it counts.
  const saveOffline = useMutation({
    networkMode: 'always',
    mutationFn: async () => {
      const userId = offlineUserId ?? useAuthStore.getState().me?.user.id;
      if (!userId) throw new Error('Sign in once while online before recording attendance offline.');
      const clientRequestId = crypto.randomUUID();
      const capturedAt = new Date().toISOString();
      const device = await offlineDeviceProof(kind === 'in' ? 'OFFLINE_CLOCK_IN' : 'OFFLINE_CLOCK_OUT', capturedAt, clientRequestId).catch(() => null);
      await queueClockEvent({
        clientRequestId,
        userId,
        kind: kind === 'in' ? 'CLOCK_IN' : 'CLOCK_OUT',
        sessionId: session.id,
        subject: sessionTitle(session),
        sessionType: isDaily ? 'DAILY' : 'CLASS',
        actualModality: kind === 'in' && !isDaily ? mode : null,
        workArrangement: kind === 'in' && isDaily ? arrangement : null,
        onlinePlatform: showsOnline ? platform || null : null,
        onlineMeetingRef: showsOnline ? meetingRef.trim() || null : null,
        method: isCoarsePointer() ? 'MOBILE_GPS' : 'WEB_BROWSER',
        location: needsLocation && gps.state === 'ok' ? gps.location : null,
        gpsError: needsLocation && gps.state !== 'ok' ? (gps.state === 'error' ? gps.error : 'UNAVAILABLE') : null,
        capturedAt,
        skewMs: currentSkewMs(),
        device,
      });
      return capturedAt;
    },
    onSuccess: (capturedAt) => {
      setSavedAt(capturedAt);
      onSaved?.();
    },
  });

  const error = submit.error;
  const networkFailed = error instanceof ApiError && error.code === 'NETWORK_ERROR';
  const canSaveOffline = actions.offlineCapture !== false;
  const failureMessage =
    error instanceof ApiError
      ? error.code === 'NETWORK_ERROR'
        ? `Your ${kind === 'in' ? 'clock-in' : 'clock-out'} was NOT recorded because the server could not be reached. Check your connection and try again — retrying is safe.`
        : error.message
      : error instanceof WebauthnProblem
        ? `${error.message} Your attendance was not recorded.`
        : error
          ? 'Something went wrong. Your attendance was not recorded.'
          : null;
  // The device's confirmation failed (or was refused): where the policy allows it, go on without.
  const confirmationFailed = error instanceof WebauthnProblem || (error instanceof ApiError && error.code === 'WEBAUTHN_INVALID');
  const what = kind === 'in' ? 'clock-in' : 'clock-out';

  if (savedAt) {
    return (
      <div className="space-y-4" aria-live="polite">
        <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4">
          <HardDriveDownload className="mt-0.5 size-6 shrink-0 text-amber-800" aria-hidden />
          <div>
            <p className="text-lg font-semibold text-amber-950">
              {kind === 'in' ? 'Clock-in' : 'Clock-out'} saved on this device at {clockTime(savedAt, timeZone)}
            </p>
            <p className="text-sm text-amber-950">
              It is not recorded yet: it will be sent when you are back online, and a reviewer checks records made offline before they count.
            </p>
          </div>
        </div>
        <div className="flex justify-end">
          <Button onClick={onClose}>Done</Button>
        </div>
      </div>
    );
  }

  if (result) {
    const time = clockTime(result.serverTimestamp, timeZone);
    return (
      <div className="space-y-4" aria-live="polite">
        <div className="flex items-start gap-3 rounded-lg border border-emerald-300 bg-emerald-50 p-4">
          <CheckCircle2 className="mt-0.5 size-6 shrink-0 text-emerald-700" aria-hidden />
          <div>
            <p className="text-lg font-semibold text-emerald-900">
              {kind === 'in' ? 'Clocked in' : 'Clocked out'} at {time}
            </p>
            <p className="text-sm text-emerald-900">Recorded with the server time. {result.replayed ? 'This request had already been recorded.' : ''}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <StatusBadge label={ATTENDANCE_STATUS[result.status].label} tone={ATTENDANCE_STATUS[result.status].tone} />
          {kind === 'in' && result.timeStatus ? <StatusBadge label={TIME_STATUS[result.timeStatus].label} tone={TIME_STATUS[result.timeStatus].tone} /> : null}
          {kind === 'out' && result.clockOutStatus ? <StatusBadge label={CLOCK_OUT_STATUS[result.clockOutStatus].label} tone={CLOCK_OUT_STATUS[result.clockOutStatus].tone} /> : null}
          {result.locationStatus ? <StatusBadge label={LOCATION_STATUS[result.locationStatus].label} tone={LOCATION_STATUS[result.locationStatus].tone} /> : null}
        </div>
        {kind === 'out' && result.session.renderedMinutes !== null ? (
          <p className="text-sm text-slate-700">
            Rendered {result.session.renderedMinutes} min of {result.session.requiredMinutes ?? result.session.scheduledMinutes} scheduled.
          </p>
        ) : null}
        {result.flags.length > 0 ? (
          <Alert tone="warning" title="Flagged for review">
            <ul className="list-disc pl-4">
              {result.flags.map((f) => (
                <li key={f.id}>{f.message}</li>
              ))}
            </ul>
          </Alert>
        ) : null}
        <div className="flex justify-end">
          <Button onClick={onClose}>Done</Button>
        </div>
      </div>
    );
  }

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (offlineOnly) saveOffline.mutate();
        else submit.mutate();
      }}
    >
      {offlineOnly ? (
        <Alert tone="warning" title="No connection">
          This record stays on the device and is sent when you are back online. Records made offline are checked by a reviewer before they count.
        </Alert>
      ) : null}
      {scan ? (
        <p className="flex items-center gap-2 rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-950">
          <QrCode className="size-4 shrink-0" aria-hidden />
          {scan.kind === 'TAG' ? `Room tag ${scan.displayName} tapped` : `Room code of ${scan.displayName} scanned`} at {clockTime(scan.scannedAt, timeZone)}.
        </p>
      ) : !offlineOnly && actions.qrScanRequired && (kind === 'in' || needsLocation) ? (
        <Alert tone="info" title="Room code">
          {kind === 'in'
            ? 'Scan the code on the display in the room (or at the building’s kiosk) with your phone’s camera to clock in.'
            : 'Scan the code on the room’s display to clock out; without it the clock-out is flagged for review.'}
        </Alert>
      ) : null}
      <div>
        <p className="text-lg font-semibold">{isDaily ? sessionTitle(session) : `${session.courseTitle ?? ''}`}</p>
        <p className="text-sm text-slate-600">
          {!isDaily ? `${sessionTitle(session)} · ` : ''}
          {clockRange(session.scheduledStart, session.scheduledEnd, timeZone)}
          {session.locationName ? ` · ${session.locationName}` : ''}
          {session.scheduledModality ? ` · ${MODALITY_LABELS[session.scheduledModality]}` : ''}
        </p>
      </div>

      {kind === 'in' && !isDaily && actions.allowedModes.length > 1 ? (
        <fieldset>
          <legend className="text-sm font-medium text-slate-800">How are you holding this class today?</legend>
          <div className="mt-2 grid gap-2 sm:grid-cols-3">
            {actions.allowedModes.map((m) => (
              <label key={m} className="flex cursor-pointer items-center gap-2 rounded-md border border-slate-300 p-3 text-sm has-[:checked]:border-blue-600 has-[:checked]:bg-blue-50">
                <input type="radio" name="actual-mode" value={m} checked={mode === m} onChange={() => setMode(m)} className="size-4" />
                {ACTUAL_MODALITY[m]}
              </label>
            ))}
          </div>
        </fieldset>
      ) : null}

      {kind === 'in' && isDaily && actions.allowedArrangements.length > 1 ? (
        <fieldset>
          <legend className="text-sm font-medium text-slate-800">Where are you working today?</legend>
          <div className="mt-2 grid gap-2 sm:grid-cols-3">
            {actions.allowedArrangements.map((a) => (
              <label key={a} className="flex cursor-pointer items-center gap-2 rounded-md border border-slate-300 p-3 text-sm has-[:checked]:border-blue-600 has-[:checked]:bg-blue-50">
                <input type="radio" name="arrangement" value={a} checked={arrangement === a} onChange={() => setArrangement(a)} className="size-4" />
                {WORK_ARRANGEMENT[a]}
              </label>
            ))}
          </div>
          {arrangement !== 'ONSITE' ? <p className="mt-2 text-xs text-slate-600">Work from home and official business are reviewed and approved separately.</p> : null}
        </fieldset>
      ) : null}

      {showsOnline ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field id="clock-platform" label="Online platform">
            {(a11y) => (
              <Select {...a11y} value={platform} onChange={(e) => setPlatform(e.target.value as OnlinePlatform | '')}>
                <option value="">Select…</option>
                {ONLINE_PLATFORM_VALUES.map((p) => (
                  <option key={p} value={p}>
                    {labelize(p)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field id="clock-meeting" label="Meeting ID (optional)">
            {(a11y) => <Input {...a11y} value={meetingRef} maxLength={200} onChange={(e) => setMeetingRef(e.target.value)} />}
          </Field>
        </div>
      ) : null}

      {needsLocation ? (
        <section aria-labelledby="clock-location" className="space-y-2 rounded-md border border-slate-200 p-3">
          <h3 id="clock-location" className="flex items-center gap-1.5 text-sm font-medium text-slate-800">
            <MapPin className="size-4" aria-hidden /> Location
          </h3>
          {disclosure ? <p className="text-xs text-slate-600">{disclosure}</p> : null}
          <div aria-live="polite" className="text-sm">
            {gps.state === 'locating' ? (
              <span className="inline-flex items-center gap-2 text-slate-700">
                <LocateFixed className="size-4 animate-pulse" aria-hidden /> Getting your location…
              </span>
            ) : null}
            {gps.state === 'ok' ? (
              <span className="text-slate-800">
                Location captured{gps.location.accuracyMeters !== null ? ` · GPS accuracy ${Math.round(gps.location.accuracyMeters)} m` : ''}. The server verifies it against the class location.
              </span>
            ) : null}
            {gps.state === 'error' ? <Alert tone="warning">{GPS_ERROR_MESSAGES[gps.error]}</Alert> : null}
          </div>
          {gps.state === 'error' || gps.state === 'ok' ? (
            <Button type="button" variant="ghost" size="sm" onClick={() => void locate()}>
              <RefreshCw aria-hidden /> Try again
            </Button>
          ) : null}
        </section>
      ) : kind === 'in' ? (
        <p className="text-sm text-slate-600">No location is needed for {isDaily ? WORK_ARRANGEMENT[arrangement].toLowerCase() : mode ? ACTUAL_MODALITY[mode].toLowerCase() : 'this session'}.</p>
      ) : null}

      {verifying ? (
        <p className="flex items-center gap-2 rounded-md border border-slate-200 p-3 text-sm text-slate-800">
          <Fingerprint className="size-4 shrink-0" aria-hidden />
          Your device will ask for your fingerprint, face or PIN to confirm it is you. It never leaves the device.
        </p>
      ) : uvRule !== 'OFF' && credentials.data && !hasCredential ? (
        <Alert tone={uvRule === 'REQUIRE' && kind === 'in' ? 'warning' : 'info'} title="Fingerprint or face">
          {uvRule === 'REQUIRE' && kind === 'in'
            ? 'Your attendance policy asks your device to confirm it is you (fingerprint, face or device PIN) when you clock in. Set it up first.'
            : `Without your device confirming it is you (fingerprint, face or device PIN), this ${what} is flagged for review.`}
          <span className="mt-1 block">
            <Link to="/account#biometrics" className="font-medium underline">
              Set up fingerprint or face
            </Link>
          </span>
        </Alert>
      ) : null}

      {failureMessage ? (
        <Alert tone="danger" title="Not recorded">
          {failureMessage}
          {error instanceof ApiError && error.code === 'DEVICE_NOT_REGISTERED' ? (
            <span className="mt-1 block">
              <Link to="/account#devices" className="font-medium underline">
                Manage your devices
              </Link>
            </span>
          ) : null}
          {error instanceof ApiError && (error.code === 'USER_VERIFICATION_REQUIRED' || error.code === 'WEBAUTHN_INVALID') ? (
            <span className="mt-1 block">
              <Link to="/account#biometrics" className="font-medium underline">
                Manage fingerprint or face
              </Link>
            </span>
          ) : null}
          {confirmationFailed && mayContinueUnconfirmed && verifying ? (
            <span className="mt-2 block">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setSkipVerification(true);
                  submit.reset();
                }}
              >
                Continue without confirming
              </Button>
              <span className="mt-1 block text-xs">The {what} is then flagged for review.</span>
            </span>
          ) : null}
          {networkFailed && canSaveOffline ? <span className="mt-1 block">You can also save it on this device and it will be sent when you are back online.</span> : null}
        </Alert>
      ) : null}
      {saveOffline.error ? <Alert tone="danger">{saveOffline.error instanceof Error ? saveOffline.error.message : 'The record could not be saved on this device.'}</Alert> : null}

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        {networkFailed && canSaveOffline && !offlineOnly ? (
          <Button type="button" variant="outline" size="lg" loading={saveOffline.isPending} disabled={gps.state === 'locating'} onClick={() => saveOffline.mutate()}>
            <HardDriveDownload aria-hidden /> Save on this device
          </Button>
        ) : null}
        <Button type="submit" size="lg" loading={offlineOnly ? saveOffline.isPending : submit.isPending} disabled={gps.state === 'locating'}>
          {offlineOnly
            ? 'Save on this device'
            : kind === 'in'
              ? isDaily
                ? 'Confirm time in'
                : 'Confirm clock in'
              : isDaily
                ? 'Confirm time out'
                : 'Confirm clock out'}
        </Button>
      </div>
    </form>
  );
}
